import { getToken } from "./auth";
import type {
  ApiErrorBody,
  BookResp,
  BookingItem,
  FlowConfig,
  Group,
  LimitConfig,
  PreviewResp,
  Ticket,
  UserClaims,
  UserSummary,
} from "./types";

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "";

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, body: ApiErrorBody | string) {
    const text = typeof body === "string" ? body : body.error;
    super(text);
    this.status = status;
    this.code = typeof body === "string" ? "unknown" : body.code;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers || {});
  const t = getToken();
  if (t) headers.set("Authorization", `Bearer ${t}`);
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");

  const resp = await fetch(`${BASE}${path}`, { ...init, headers });
  if (!resp.ok) {
    let body: ApiErrorBody | string = resp.statusText;
    try {
      body = (await resp.json()) as ApiErrorBody;
    } catch {
      // ignore
    }
    throw new ApiError(resp.status, body);
  }
  if (resp.status === 204) return undefined as unknown as T;
  return (await resp.json()) as T;
}

// ============================================================================
// Endpoints
// ============================================================================

export const api = {
  me: () => request<UserClaims>("/api/me"),

  bookingItems: {
    list: (limit = 50, offset = 0) =>
      request<BookingItem[]>(`/api/booking-items?limit=${limit}&offset=${offset}`),
    get: (id: string, fresh = false) =>
      request<BookingItem>(`/api/booking-items/${id}${fresh ? "?fresh=1" : ""}`),
    preview: (id: string) =>
      request<PreviewResp>(`/api/booking-items/${id}/preview`),
    create: (body: {
      name: string;
      description?: string | null;
      total_slots: number;
      group_id?: string | null;
    }) =>
      request<BookingItem>("/api/booking-items", { method: "POST", body: JSON.stringify(body) }),
    update: (
      id: string,
      body: {
        name: string;
        description?: string | null;
        total_slots: number;
        group_id?: string | null;
      }
    ) => request<BookingItem>(`/api/booking-items/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    delete: (id: string) =>
      request<{ ok: boolean }>(`/api/booking-items/${id}`, { method: "DELETE" }),
  },

  tickets: {
    book: (itemId: string, flowToken?: string | null) =>
      request<BookResp>(`/api/booking-items/${itemId}/book`, {
        method: "POST",
        body: flowToken ? JSON.stringify({ flow_token: flowToken }) : undefined,
      }),
    confirm: (ticketId: string) =>
      request<{ ticket_id: string; availability: number }>(`/api/tickets/${ticketId}/confirm`, {
        method: "POST",
      }),
    cancel: (ticketId: string) =>
      request<{ availability: number }>(`/api/tickets/${ticketId}`, { method: "DELETE" }),
    void: (ticketId: string, reason: string) =>
      request<{ availability: number }>(`/api/tickets/${ticketId}/void`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      }),
    mine: (limit = 50, offset = 0) =>
      request<Ticket[]>(`/api/me/tickets?limit=${limit}&offset=${offset}`),
    byItem: (itemId: string, limit = 50, offset = 0) =>
      request<Ticket[]>(`/api/tickets?booking_item_id=${itemId}&limit=${limit}&offset=${offset}`),
    byUser: (userId: string, limit = 50, offset = 0) =>
      request<Ticket[]>(`/api/tickets?user_id=${userId}&limit=${limit}&offset=${offset}`),
  },

  users: {
    list: (limit = 20, offset = 0) =>
      request<UserSummary[]>(`/api/users?limit=${limit}&offset=${offset}`),
  },

  rules: {
    getLimits: () => request<LimitConfig>("/api/admin/rules/limits"),
    putLimits: (cfg: LimitConfig) =>
      request<LimitConfig>("/api/admin/rules/limits", {
        method: "PUT",
        body: JSON.stringify(cfg),
      }),
  },

  groups: {
    list: (limit = 100, offset = 0) =>
      request<Group[]>(`/api/groups?limit=${limit}&offset=${offset}`),
    get: (id: string) => request<Group>(`/api/groups/${id}`),
    create: (body: { name: string; description?: string | null }) =>
      request<Group>("/api/groups", { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: { name: string; description?: string | null }) =>
      request<Group>(`/api/groups/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    delete: (id: string) =>
      request<{ ok: boolean }>(`/api/groups/${id}`, { method: "DELETE" }),
    getFlow: (id: string) => request<FlowConfig | null>(`/api/groups/${id}/flow`),
    setFlow: (id: string, cfg: FlowConfig) =>
      request<Group>(`/api/groups/${id}/flow`, {
        method: "PUT",
        body: JSON.stringify(cfg),
      }),
    clearFlow: (id: string) =>
      request<Group>(`/api/groups/${id}/flow`, { method: "DELETE" }),
  },

  flows: {
    list: (limit = 100, offset = 0) =>
      request<Group[]>(`/api/flows?limit=${limit}&offset=${offset}`),
  },
};
